"use client";

import Button from "@/components/ui/Button";
import Image from "@/components/ui/Image";
import InputBorder from "@/components/ui/InputBorder";
import { useAppSelector } from "@/redux/hook";
import { ProductJson } from "@/types/Product.type";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import ProductTags from "~components/product/ProductTags";
import { notification } from "@/lib/api/shop/customer";
import BaseApi from "@/common/api/BaseApi";
import ImageConstant from "@/common/constants/image";
import useCustomer from "@/lib/hooks/useCustomer";

interface Props {
	product_variant: ProductJson;
	onSendMailFinish?: (email: string, status: "success" | "fail") => void;
}

type SendMailForm = {
	email: string;
};

export default function ProductModalOutOfStock({
	product_variant,
	onSendMailFinish,
}: Props) {
	const { data: user } = useCustomer();

	const { control, handleSubmit, reset } = useForm<SendMailForm>({
		defaultValues: {
			email: user?.email ? user.email : "",
		},
	});

	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [isSent, setIsSent] = useState(false);

	const [email, setEmail] = useState(user?.email || "");
	const [errors, setErrors] = useState<string[]>([]);

	const [show, setShow] = useState(false);

	useEffect(() => {
		setTimeout(() => {
			setShow(true);
		}, 0);
	}, []);

	const handleSendMail = async (formData: SendMailForm) => {
		setErrors([]);
		setSuccess(false);
		setLoading(true);
		setEmail(formData.email);

		try {
			const res = await notification({
				contact: formData.email,
				item_id: product_variant.id,
				type: 1,
			});
			if (res.data.length > 0) {
				setSuccess(true);
				errors.length > 0 && setErrors([]);
				onSendMailFinish && onSendMailFinish(formData.email, "success");
			}
		} catch (error) {
			const dataError = BaseApi.handleError(error);
			setErrors(dataError.errors);
			onSendMailFinish && onSendMailFinish(formData.email, "fail");
		}

		reset();
		setLoading(false);
	};
	return (
		<div>
			<h1 className="text-center text-sm leading-[1.6] font-bold text-gray-500 capitalize mb-2">
				Gửi Email Khi Có Hàng
			</h1>

			<div className="px-4 py-2 border-y border-gray-300 flex gap-2 justify-center">
				<Image
					height={100}
					width={100}
					src={
						product_variant.images.length > 0
							? product_variant.images[0].url
							: ""
					}
					alt={"image"}
					className="w-[100px] h-[100px] object-contain"
					placeholder={"blur"}
					blurDataURL={ImageConstant.BlurDataURL}
				/>

				<div className="flex-1 flex-col gap-[2px]">
					<ProductTags tags={product_variant.tags} className="mb-[2px]" />
					<h4 className="text-sm leading-[1.6] text-gray-500 font-bold line-clamp-1">
						{product_variant.name}
					</h4>
					<p className="text-sm leading-[1.6] text-gray-500 line-clamp-1">
						Thương hiệu:{" "}
						{product_variant?.brand && (
							<span className="font-bold">{product_variant?.brand?.title}</span>
						)}
					</p>
					<p className="text-sm leading-[1.6] text-gray-500 line-clamp-1">
						{product_variant.option_name
							? product_variant.option_name
							: product_variant.full_name}
					</p>
					<p className="text-sm leading-[1.6] text-gray-400 line-clamp-1">
						Mã hàng : {product_variant.sku}
					</p>
				</div>
			</div>
			<div className="pt-2">
				<p className="text-center text-gray-500 mb-4 text-sm leading-[1.6]">
					Nhập địa chỉ email để nhận email thông báo khi có hàng
				</p>
				{!success && errors.length <= 0 && (
					<form
						onSubmit={handleSubmit(handleSendMail)}
						className="flex items-center justify-between gap-2">
						{show && (
							<InputBorder
								rules={{
									required: "Vui lòng nhập Email",
									pattern: {
										value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
										message: "Địa chỉ email không hợp lệ",
									},
								}}
								inputClassName="h-full px-[14px]"
								name="email"
								control={control}
								placeholder="Nhập email của bạn"
								className="border-gray-300 flex-1 w-full h-[34px] px-0 py-0 text-base leading-[1.6]"
							/>
						)}
						<Button
							loading={loading}
							type="submit"
							label="Gửi"
							mode="main"
							className="min-w-fit md:min-w-[158px] h-[34px] px-[14px] py-2 text-sm leading-[1.6]"
						/>
					</form>
				)}
			</div>
			{errors.length > 0 ? (
				<div className="text-gray-500 mt-4 font-semibold text-sm">
					<p>
						Chúng tôi sẽ gửi mail cho bạn sớm!{" "}
						{
							<span
								onClick={() => {
									setErrors([]);
									setSuccess(false);
								}}
								className="text-blue-500 strong underline cursor-pointer">
								Gửi lại
							</span>
						}
					</p>
				</div>
			) : (
				success &&
				email.length > 0 && (
					<div className="text-sm mt-4">
						<p>
							Chúng tôi sẽ gửi đến email{" "}
							<span className="font-bold">{email}</span> ngay khi sản phẩm có
							hàng.{" "}
							{success && (
								<span
									onClick={() => {
										setSuccess(false);
									}}
									className="text-blue-500 strong underline cursor-pointer">
									Gửi lại
								</span>
							)}
						</p>
					</div>
				)
			)}
		</div>
	);
}
